import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MessageSquare, Send, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Single source of truth for the default WhatsApp text used across the CRM.
 * Personalises the greeting with the customer's name when available.
 */
export const buildCollectionReadyMessage = (customerName) => {
  const name = (customerName || "").trim();
  const greet = name ? `Hello ${name},` : "Hello,";
  return `${greet}\nYour document is ready for collection. Please visit our office between 10:00 AM to 7:00 PM at your convenience to collect it.`;
};

/**
 * Reusable WhatsApp compose dialog. Pre-fills a template message that the staff
 * can edit before hitting Send. Uses wa.me deep-link — the phone number is used
 * as-is (only non-digit characters are stripped). Add country code in the stored
 * mobile if required.
 *
 * Props:
 *  - open, onOpenChange
 *  - mobile        (string) — customer phone number as stored
 *  - customerName  (string) — used only in header/subtitle
 *  - defaultMessage(string) — pre-fills the textarea; user can edit
 *  - title         (string) — modal title (optional)
 *  - subtitle      (string) — small line under title (optional)
 *  - onSent        (fn)     — called after the wa.me tab is opened
 */
export default function WhatsAppMessageModal({
  open,
  onOpenChange,
  mobile,
  customerName,
  defaultMessage,
  title = "Send WhatsApp Message",
  subtitle,
  onSent,
}) {
  const [message, setMessage] = useState(defaultMessage || "");

  // Reset when opened (so a fresh default is loaded each time)
  useEffect(() => {
    if (open) setMessage(defaultMessage || "");
  }, [open, defaultMessage]);

  const send = () => {
    const raw = String(mobile || "").replace(/\D/g, "");
    if (!raw) {
      toast.error("No mobile number available for this customer");
      return;
    }
    const text = (message || "").trim();
    if (!text) {
      toast.error("Message cannot be empty");
      return;
    }
    // Use mobile as-is (already includes country code if needed)
    window.open(`https://wa.me/${raw}?text=${encodeURIComponent(text)}`, "_blank");
    if (typeof onSent === "function") onSent();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="whatsapp-message-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <MessageSquare className="h-5 w-5 text-emerald-600" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {customerName && <span className="font-semibold text-slate-800">{customerName}</span>}
            {mobile && <span className="ml-2 font-mono-num text-slate-500">· {mobile}</span>}
            {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Message (edit before sending)
            </Label>
            <button
              type="button"
              onClick={() => setMessage(defaultMessage || "")}
              className="text-[10px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
              data-testid="whatsapp-reset-btn"
              title="Reset to default template"
            >
              <RotateCcw className="h-3 w-3" /> Reset
            </button>
          </div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={7}
            className="w-full border border-slate-200 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            data-testid="whatsapp-message-textarea"
          />
          <div className="text-[10px] text-slate-400">
            {message.length} characters · Line breaks preserved
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} variant="outline" data-testid="whatsapp-cancel-btn">
            <X className="h-4 w-4 mr-1" /> Cancel
          </Button>
          <Button
            onClick={send}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            data-testid="whatsapp-send-btn"
          >
            <Send className="h-4 w-4 mr-1" /> Send via WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
